<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { Text, Modal, SelectCard, CardGrid } from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';

	import { home_data } from 'src/stores/account/user';
	import { createMemberStatusSnapshot } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { includeDeviceModal } from 'src/stores/features/inclusion';
	import CameraMonitoringDashboard from './MonitoringDashboard/CameraMonitoringDashboard.svelte';
	import CameraMonitoringToolbar from './MonitoringDashboard/CameraMonitoringToolbar.svelte';
	import CameraSettingsModal from './MonitoringDashboard/CameraSettingsModal.svelte';
	import EmptyList from '../Lists/EmptyList.svelte';
	import EmptyPlaceholder from 'src/components/EmptyPlaceholder.svelte';
	import { format } from 'svelte-i18n';
	import {
		getCameraMonitoringSettings,
		saveCameraMonitoringSettings,
		defaultCameraMonitoringSettings,
		CAMERA_DEFAULT_DASHBOARD_ID,
		ensureDefaultCameraDashboard,
		type GridLayout,
	} from './helpers/camera-monitoring-settings';
	import { cameraMonitoringActions, cameraMonitoringEditMode } from './helpers/camera-monitoring-ui';
	import { getCameraDevices, isCameraOnline } from './helpers/camera-helpers';
	import { CAMERA_CLOUD_STORAGE_SHOP_URL } from 'src/helpers/camera_cloud_storage_helpers';
	import { navigateToShop } from 'src/components/Lists/list-helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	export let dashboardId: string | number | undefined = CAMERA_DEFAULT_DASHBOARD_ID;

	// Grid layout options. `focus` is a spotlight layout (1 large + up to 5 small
	// in a 3x3 grid); `cols` is only meaningful for the uniform grids.
	const allGridLayouts: { value: GridLayout; icon: string; cols: number }[] = [
		{ value: '1x1', icon: 'fa-solid fa-square', cols: 1 },
		{ value: '2x2', icon: 'fa-solid fa-grid-2', cols: 2 },
		{ value: '3x3', icon: 'fa-solid fa-grid-3', cols: 3 },
		{ value: '4x4', icon: 'fa-solid fa-grid-4', cols: 4 },
		{ value: 'focus', icon: 'fa-solid fa-table-layout', cols: 3 },
	];

	let showSettingsModal = false;
	let showModalMenuGrouped = false;
	let windowInnerWidth = 0;

	// DS breakpoints:
	$: isMobile = windowInnerWidth < SystemScreenBreakpoints.MOBILE_MAX;

	// Dropdown menu data structure (like DS demo pattern)
	$: dropdownActionsData = [
		{
			groupTitle: $format('label_menu_items_dashboard', { default: 'Dashboard' }),
			isGroupTitle: true,
			items: [
				{
					id: 'camera-settings',
					title: $format('button_customize_widget', { default: 'Customize' }),
					icon: 'fas fa-sliders',
					action: openSettings,
				},
				{
					id: 'edit-order',
					title: $format('dropdown_reorder_cameras', { default: 'Reorder cameras' }),
					icon: 'fas fa-arrows-up-down-left-right',
					action: toggleEditMode,
				},
			],
		},
	];

	// Get all camera devices from store
	$: allCameraDevices = getCameraDevices($devices) || [];
	$: cameraIds = allCameraDevices.map((d) => d.id);
	$: memberStatusStore = createMemberStatusSnapshot(cameraIds);
	$: memberStatus = $memberStatusStore;

	// Derive settings reactively from home_data
	$: settings = getCameraMonitoringSettings($home_data, dashboardId);
	$: preferredLayout = settings.preferredLayout;
	$: hideOfflineCameras = settings.hideOfflineCameras;
	$: cameras = settings.cameras;
	$: focusedCameraId = settings.focusedCameraId;

	// Derive visible camera IDs as Set for quick lookup (empty array = all cameras)
	$: visibleCameraIds = new Set(!cameras?.length ? allCameraDevices.map((d) => d.id) : cameras);

	// Derive camera order (use saved order, or default to all cameras)
	$: cameraOrder = !cameras?.length ? allCameraDevices.map((d) => d.id) : cameras;

	// Filter cameras based on visibility settings and online status, respecting order
	$: displayedCameras = cameraOrder
		.map((id) => allCameraDevices.find((d) => d.id === id))
		.filter((camera): camera is (typeof allCameraDevices)[0] => {
			if (!camera) return false;
			if (!visibleCameraIds.has(camera.id)) {
				return false;
			}
			if (hideOfflineCameras && !isCameraOnline(memberStatus[camera.id] as any)) {
				return false;
			}
			return true;
		});

	// Base grid layouts (1x1–4x4) are always offered on larger viewports so the
	// layout picker is available regardless of camera count. Phone (≤639) keeps
	// 1x1 plus focus (main + 2-up secondary strip); multi-column walls don't fit.
	$: availableGridLayouts = allGridLayouts.filter((layout) => {
		if (isMobile) return layout.cols === 1 || layout.value === 'focus';
		return true;
	});

	// Effective layout: use preferred if available, otherwise fallback to largest available
	$: selectedLayout = availableGridLayouts.some((l) => l.value === preferredLayout)
		? preferredLayout
		: availableGridLayouts[availableGridLayouts.length - 1]?.value ||
			defaultCameraMonitoringSettings.preferredLayout;

	// Get current layout configuration
	$: currentLayout = allGridLayouts.find((l) => l.value === selectedLayout) || allGridLayouts[1];

	// Phone: 1 column only (DS breakpoint)
	$: effectiveCols = isMobile ? 1 : currentLayout.cols;

	function handleLayoutChange(layout: GridLayout) {
		saveCameraMonitoringSettings({ preferredLayout: layout }, dashboardId);
	}

	function openSettings() {
		showSettingsModal = true;
		showModalMenuGrouped = false;
	}

	function closeSettings() {
		showSettingsModal = false;
		showModalMenuGrouped = false;
	}

	function handleSettingsSave(event: CustomEvent<{ cameras: string[]; hideOfflineCameras: boolean }>) {
		const { cameras: newCameras, hideOfflineCameras: newHideOffline } = event.detail;
		saveCameraMonitoringSettings(
			{ cameras: newCameras, hideOfflineCameras: newHideOffline },
			dashboardId,
		);
	}

	function toggleEditMode() {
		cameraMonitoringEditMode.update((v) => !v);
	}

	function toggleHideOffline() {
		saveCameraMonitoringSettings({ hideOfflineCameras: !hideOfflineCameras }, dashboardId);
	}

	function handleFocusCamera(event: CustomEvent<{ id: string }>) {
		saveCameraMonitoringSettings({ focusedCameraId: event.detail.id }, dashboardId);
	}

	function onAddCameraClick() {
		includeDeviceModal.update((state) => ({ ...state, show: true }));
	}

	// Publish the dashboard's action surface so the shared dashboard Tabs kebab
	// (rendered by MainLayoutNew) can drive layout/settings without a dedicated
	// toolbar. Legacy layout keeps its inline CameraMonitoringToolbar instead.
	$: if ($showNewLayout) {
		cameraMonitoringActions.set({
			availableGridLayouts,
			selectedLayout,
			hideOfflineCameras,
			hasCameras: allCameraDevices.length > 0,
			onLayoutChange: handleLayoutChange,
			onOpenSettings: openSettings,
			onToggleHideOffline: toggleHideOffline,
		});
	}

	onDestroy(() => {
		cameraMonitoringActions.set(null);
		cameraMonitoringEditMode.set(false);
	});

	function handleGridReorder(event: CustomEvent) {
		// Dedupe defensively: a transient DnD glitch can momentarily yield the same
		// id twice, and persisting that would make the keyed grid crash on render.
		const orderedIds: string[] = event.detail.items.map((item: { id: string }) => item.id);
		const newOrder: string[] = [...new Set(orderedIds)];
		saveCameraMonitoringSettings({ cameras: newOrder }, dashboardId);
	}

	onMount(() => {
		if (Number(dashboardId) === CAMERA_DEFAULT_DASHBOARD_ID) {
			void ensureDefaultCameraDashboard();
		}
	});
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

<div class="camera-monitoring-route width-full">
	{#if !$showNewLayout}
		<CameraMonitoringToolbar
			editMode={$cameraMonitoringEditMode}
			{availableGridLayouts}
			{selectedLayout}
			{dropdownActionsData}
			on:layout-change={(e) => handleLayoutChange(e.detail.layout)}
			on:toggle-edit={toggleEditMode}
		/>
	{/if}

	{#if allCameraDevices.length === 0}
		<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column align-items-center">
			<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
			<EmptyList
				title={$format('empty_no_cameras_found', { default: 'No cameras yet' })}
				description={$format('empty_no_cameras_found_descr', {
					default: 'Add a camera device to start monitoring your home.',
				})}
				btnText={capFirstChar($format('button_add_device', { default: 'Add device' }))}
				onClick={onAddCameraClick}
			/>
			<span
				role="none"
				class="link-styles mt-s"
				on:click={() => navigateToShop(CAMERA_CLOUD_STORAGE_SHOP_URL)}
			>
				{$format('label_browse_shelly_store', { default: 'Browse the Shelly store' })}
			</span>
		</div>
	{:else}
		{#key selectedLayout}
			<CameraMonitoringDashboard
				cameras={displayedCameras}
				cols={effectiveCols}
				layout={selectedLayout}
				{focusedCameraId}
				editMode={$cameraMonitoringEditMode}
				onOpenSettings={openSettings}
				on:reorder={handleGridReorder}
				on:focus={handleFocusCamera}
			/>
		{/key}
	{/if}
</div>

<CameraSettingsModal
	showModal={showSettingsModal}
	{cameras}
	{hideOfflineCameras}
	on:close={closeSettings}
	on:save={handleSettingsSave}
/>

<Modal bind:showModal={showModalMenuGrouped} hideTitle on:close={() => (showModalMenuGrouped = false)}>
	<svelte:fragment slot="content">
		{#each dropdownActionsData as group, index}
			<Text styleType="label-large" class="mb-s">
				{group.groupTitle}
			</Text>
			<CardGrid md={250} noPaddingBottom gridGap="s">
				{#each group.items as item}
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						hideAction
						class="width-full"
						type="horizontal"
						title={item.title}
						icon={item.icon}
						on:click={item.action}
					/>
				{/each}
			</CardGrid>
			{#if index < dropdownActionsData.length - 1}
				<hr class="mb-l mt-l" />
			{/if}
		{/each}
	</svelte:fragment>
</Modal>

<style>
	.camera-monitoring-route {
		/* Mosaic measures remaining viewport height itself; keep chrome padding minimal. */
		padding-bottom: 0;
	}
</style>
